import { openedSessionTabAtom, openedSessionTabEnum } from '@/atoms/sideNavAtoms';
import { useSessionsFilteredList } from '@/components/layout/Navigation/sessions/hooks/useSessionsFilteredList';
import { SessionsListAssistantView } from '@/components/layout/Navigation/sessions/side-nav-list/components/sessions-list-assistant-view';
import { SideNavSessionsList } from '@/components/layout/Navigation/sessions/side-nav-list/side-nav-sessions-list';
import { useShowSessionsListAssistantView } from '@/composition/hosts/session/session-list/useShowSessionsListAssistantView';
import { SessionCreationInProgressBanner } from '@/features/sessions/components/session-creation-in-progress-banner';
import SessionBarUpcomingTabV2 from '@/features/upcomingSessionTab/components/session-bar-upcoming-tab-v2';
import SessionsHeaderV2 from '@/features/upcomingSessionTab/components/sessions-header-v2';
import { UploadDiscoveryTooltip } from '@/features/upcomingSessionTab/components/upload-discovery-tooltip';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { isOnPrem } from '@/utils/buildProfile';
import { Badge } from '@heidi/design-system/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@heidi/design-system/tabs';
import { TypographyCaptionBold, TypographyP } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { isSessionSearchBarOpenAtom } from '@heidi/session';
import { useAtom, useAtomValue } from 'jotai';
import { FlaskConical } from 'lucide-react';
import { memo, useMemo } from 'react';

const SessionsBarV2 = memo(() => {
  const { t } = useTranslations();
  const [openedSessionTab, setOpenedSessionTab] = useAtom(openedSessionTabAtom);
  const isMobile = useIsMobile();
  const { sessionSearchResult, searchValue } = useSessionsFilteredList();
  const isSessionSearchBarOpen = useAtomValue(isSessionSearchBarOpenAtom);

  const showSessionsListAssistantView = useShowSessionsListAssistantView();

  const height = useMemo(() => {
    return !isSessionSearchBarOpen ? 'calc(100% - 32px)' : 'calc(100% - 62px)';
  }, [isSessionSearchBarOpen]);

  // On-prem: no EHR integration or appointment scheduling — skip the Schedule
  // tab entirely and show the sessions list directly.
  if (isOnPrem) {
    return (
      <div className="flex size-full w-full min-w-[182px] flex-col gap-2 sm:gap-1">
        <SessionsHeaderV2 />
        <div style={{ height }} className="mt-1.5 flex flex-col">
          <SessionCreationInProgressBanner />
          <div className="min-h-0 flex-1">
            <SideNavSessionsList />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex size-full w-full min-w-[182px] flex-col gap-2 sm:gap-1">
      <SessionsHeaderV2 />

      {/* tabs for sessions */}
      <Tabs
        className="w-full transition-all ease-in-out"
        value={openedSessionTab}
        onValueChange={(e) => {
          setOpenedSessionTab(e as openedSessionTabEnum);
        }}
        style={{
          height,
        }}
      >
        <TabsList className="h-fit w-full bg-transparent py-0">
          <UploadDiscoveryTooltip>
            <TabsTrigger
              value={openedSessionTabEnum.UPCOMING}
              data-testid="sessions-upcoming-tab"
              className="w-full rounded-none border-b-2 border-outline hover:text-text-primary data-[state=active]:border-border-selected data-[state=active]:bg-transparent data-[state=active]:shadow-none"
            >
              <div className="flex items-center gap-1.5">
                {isMobile ? (
                  <TypographyP>{t('sessions.upcoming')}</TypographyP>
                ) : (
                  <TypographyCaptionBold>
                    {t('sessions.upcoming')}{' '}
                    {searchValue &&
                      sessionSearchResult &&
                      `(${sessionSearchResult.length})`}
                  </TypographyCaptionBold>
                )}
                <Badge color="beta" size="xs" className="gap-0 p-1">
                  <FlaskConical size={8} />
                </Badge>
              </div>
            </TabsTrigger>
          </UploadDiscoveryTooltip>
          <TabsTrigger
            value={openedSessionTabEnum.PAST}
            className="w-full rounded-none border-b-2 border-outline hover:text-text-primary data-[state=active]:border-border-selected data-[state=active]:bg-transparent data-[state=active]:shadow-none"
          >
            {isMobile ? (
              <TypographyP>{t('sessions.past')}</TypographyP>
            ) : (
              <TypographyCaptionBold>
                {t('sessions.past')}{' '}
                {searchValue && sessionSearchResult && `(${sessionSearchResult.length})`}
              </TypographyCaptionBold>
            )}
          </TabsTrigger>
        </TabsList>
        <TabsContent
          value={openedSessionTabEnum.UPCOMING}
          style={{ height: 'calc(100% - 36px)' }}
          className="mt-1.5"
        >
          {showSessionsListAssistantView ? (
            <SessionsListAssistantView />
          ) : (
            <SessionBarUpcomingTabV2 />
          )}
        </TabsContent>
        <TabsContent
          value={openedSessionTabEnum.PAST}
          style={{ height: 'calc(100% - 36px)' }}
          className="mt-1.5 flex flex-col"
        >
          <SessionCreationInProgressBanner />
          <div className="min-h-0 flex-1">
            <SideNavSessionsList />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
});

SessionsBarV2.displayName = 'SessionsBarV2';
export default SessionsBarV2;
